import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent, CartAddEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {SVGUseElement} cartIconSvg - The cart icon SVG use element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount', 'cartIconSvg'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent | CartAddEvent} event - The cart update event.
   */
  onCartUpdate = async (/** @type {CartUpdateEvent | CartAddEvent} */ event) => {
    if (event.target === this) return;

    const { itemCount } = event.detail.data;
    
    // CartAddEvent means items were added (itemCount is quantity added)
    // CartUpdateEvent means cart was updated (itemCount is total count)
    // Use constructor name for more reliable type checking across module boundaries
    const isAddEvent = event.constructor.name === 'CartAddEvent';

    if (isAddEvent) {
      // For add events, fetch the actual cart count from server to ensure accuracy
      // This prevents race conditions where the optimistic update might be wrong
      await this.#fetchCartCount();
    } else {
      // For update events, use the provided total count
      this.renderCartBubble(itemCount || 0, false);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    // Update the SVG icon based on cart count
    this.updateCartIcon(this.currentCartCount);

    // Update the actual text content of the cart bubble
    if (this.currentCartCount > 0 && this.currentCartCount <= 99) {
      this.refs.cartBubbleCount.textContent = this.currentCartCount.toString();
    } else if (this.currentCartCount > 99) {
      this.refs.cartBubbleCount.textContent = '99+';
    } else {
      this.refs.cartBubbleCount.textContent = '';
    }

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Updates the cart icon SVG based on cart count
   * @param {number} count - The current cart count
   */
  updateCartIcon = (count) => {
    const iconId = count > 0 ? '#icon_cart_full' : '#icon_cart';
    if (this.refs.cartIconSvg) {
      this.refs.cartIconSvg.setAttribute('xlink:href', iconId);
    }
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = async () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent.trim();

    // If no sessionStorage, fetch from server
    if (sessionStorageCount === null) {
      await this.#fetchCartCount();
      return;
    }

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      const storedCount = String(value);

      // Check if sessionStorage is stale (older than 10 seconds)
      if (Date.now() - timestamp >= 10000) {
        await this.#fetchCartCount();
        return;
      }

      // If visible count matches stored count, we're good
      if (visibleCount === storedCount || visibleCount === '99+') {
        return;
      }

      // Update to match sessionStorage
      const count = parseInt(value, 10);
      if (count >= 0) {
        this.renderCartBubble(count, false, false);
      }
    } catch (_) {
      // If parsing fails, fetch from server
      await this.#fetchCartCount();
    }
  };

  /**
   * Fetches the actual cart count from the server
   */
  #fetchCartCount = async () => {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const cart = await response.json();
      
      if (cart && typeof cart.item_count === 'number') {
        this.renderCartBubble(cart.item_count, false, false);
      }
    } catch (error) {
      console.error('Failed to fetch cart count:', error);
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
